import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { queryWishlistMaterials } from '../../../../api/wishlist/query-wishlist-materials';
import { DEFAULT_SORTING_KEYS } from '../../../../constants/search-sorting';
import session from '../../../../utils/session';

const PAGE_SIZE = 10;
const SORTING = DEFAULT_SORTING_KEYS.CREATED_AT_DESC;

const fetchPage = (p: number, limit = PAGE_SIZE) =>
    queryWishlistMaterials({ limit, p, s: SORTING } as unknown as SearchState, {
        filter: '{ isFree: false }'
    });

type UseFavoritesReturn = {
    materials: Material[];
    hasMoreToFetch: boolean;
    onFetchNextPage: VoidFunction;
    onRemove: (material: Material) => void;
};

const useFavorites = (): UseFavoritesReturn => {
    const [materials, setMaterials] = useState<Material[]>([]);
    const [total, setTotal] = useState(0);

    const hasFetched = useRef<boolean>(false);
    const prevLengthRef = useRef<number>(0);

    const favoriteList = useSelector((state: RootState) => state.main.favorite.list);

    const handleFetchFirstPage = (): void => {
        hasFetched.current = true;
        prevLengthRef.current = favoriteList.length;

        fetchPage(1).then(({ total: serverTotal, wishlist }) => {
            setTotal(serverTotal);
            setMaterials(wishlist);
        });
    };

    const handleAddMaterial = (): void => {
        const addedMaterial = favoriteList[0] as unknown as Material;
        const isPaidAndNotAcquired = !addedMaterial.isFree && !addedMaterial.aclId;

        prevLengthRef.current = favoriteList.length;

        if (!isPaidAndNotAcquired) {
            return;
        }

        setTotal((prevTotal) => prevTotal + 1);
        setMaterials((prev) => [addedMaterial, ...prev]);
    };

    useEffect(() => {
        const isInitialFetch = !hasFetched.current && favoriteList.length > 0 && session.isLoggedIn();
        const isMaterialAdded = hasFetched.current && favoriteList.length > prevLengthRef.current;

        if (isInitialFetch) {
            handleFetchFirstPage();

            return;
        }

        if (isMaterialAdded) {
            handleAddMaterial();

            return;
        }

        prevLengthRef.current = favoriteList.length;
    }, [favoriteList.length]);

    const onFetchNextPage = (): void => {
        const nextPage = Math.floor(materials.length / PAGE_SIZE) + 1;
        const offset = materials.length % PAGE_SIZE;

        fetchPage(nextPage).then(({ wishlist }) => setMaterials((prev) => [...prev, ...wishlist.slice(offset)]));
    };

    const onRemove = (material: Material): void => {
        const newMaterials = materials.filter((m) => m.id !== material.id);
        const newTotal = total - 1;

        setTotal(newTotal);
        setMaterials(newMaterials);

        const shouldFetchNextMaterial = newMaterials.length < newTotal;

        if (!shouldFetchNextMaterial) {
            return;
        }

        fetchPage(newMaterials.length + 1, 1).then(({ wishlist }) => setMaterials([...newMaterials, wishlist[0]]));
    };

    const hasMoreToFetch = materials.length < total;

    return { materials, hasMoreToFetch, onFetchNextPage, onRemove };
};

export default useFavorites;
